import { ApiError } from "src/core/apiError";
import { logger } from "src/core/globals";
import {
  Error,
  MarkerType,
  Message,
  Request
} from "src/gen/schema/syncfollow/syncfollow";
import { generateDataId } from "src/utils/data";
import { throttle, ThrottledCallback } from "src/utils/timing";

import { Observable } from "./observable";
import { StreamClient } from "./streamClient";
import {
  ErrorHandler,
  EVT,
  MetaHandler,
  StreamReducer,
  StreamTransformer,
  UpdateHandler
} from "./types";

/**
 * This class is designed to manage
 * streaming data, handling the lifecycle
 * of streams, and broadcasting updates
 * to subscribed clients.
 */
export class Stream<
  TRequest extends Message,
  TData
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
> extends Observable<any> {
  static END: EVT = "end";
  private clients: Map<string, StreamClient<TData>> = new Map();
  private parseMessage: (m: Message) => void;
  private broadcast: ThrottledCallback<[]>;
  private message: TRequest;
  private cleanupTimeoutId: NodeJS.Timeout | null = null;

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  public data: any;
  public count: number = 0;
  public sequence: number = 0;
  public total: number = 0;
  public syncId: string = "";
  public lastMessage?: Date;
  public marker: MarkerType | undefined;

  /**
   * Creates a new Stream instance for managing streaming data and broadcasting updates to clients.
   *
   * @param message - The streaming request message wrapped in a Message object
   * @param transformer - Function that transforms incoming Message objects into TData format
   * @param reducer - Function that reduces transformed data into the stream's accumulated state
   * @param interval - Throttle interval in milliseconds for broadcasting updates to clients (default: 1000ms)
   */
  constructor(
    message: TRequest,
    transformer: StreamTransformer<Message, TData>,
    reducer: StreamReducer<TData>,
    interval: number = 1000
  ) {
    super();
    /**
     * Need to be able to message the StreamManager
     * that this stream is done. This is done using
     * the END Event
     */
    this.addEvents([Stream.END]);
    /**
     * This is the streaming Request
     * wrapped in a Message.
     */
    this.message = message;
    /**
     * Need to get a little creative here, making
     * the syncId the hashed request.
     */
    this.syncId = generateDataId(message);
    /**
     * Internal method to parse the message
     * before it is broadcast to the consumers
     */
    this.parseMessage = (m: Message) => {
      const data = transformer(m);
      this.sequence = Number(m.data?.sequence ?? 0); // note well: backend has typed total count as a 64-bit long
      if (data) {
        this.data = Object.freeze(reducer(this.data, data));
      }
    };

    /**
     * Only update clients based on
     * the broadcast interval
     */
    this.broadcast = throttle(() => {
      const { data, sequence, total } = this;
      const syncing = this.isSyncing();
      this.clients.forEach((client) => {
        client.updateCb(data, syncing, total, sequence);
      });
    }, interval);

    /**
     * This is a timer to detect if the stream was
     * initiated but after 15 seconds it not received
     * a markers. This is a signal that the subscription
     * never initialized and the underlying provider might
     * be stuck.
     *
     * No real cleanup since the cleanup timeout is
     * 10 seconds and this is 15. There is little point
     * to the overhead of clearing the timeout.
     */
    setTimeout(() => {
      if (this.marker === undefined) {
        const apiError = ApiError.fromMessage("Stream was never initialized");
        this.clients.forEach((client) => {
          client.errorCb(apiError);
        });
      }
    }, 15000);
  }

  getRequest = (): Request | undefined => this.message.request;

  getStringifiedRequest = (): string => {
    /**
     * Setting the syncId in the request
     * alters the object which will cause
     * issues. Instead, to we can keep track
     * of what messages are for which consumers
     * I'm making it part of the stringified
     * message to the websocket and keeping
     * the original request unchanged.
     */
    return JSON.stringify({
      request: { ...this.message.request, syncId: this.syncId }
    });
  };

  getBinaryStringifiedRequest = (): Uint8Array => {
    return Message.encode(
      Message.fromJSON({
        request: { ...this.message.request, syncId: this.syncId }
      })
    ).finish();
  };

  /**
   * The followers method returns the number
   * of clients subscribed to the stream
   */
  followers = (): number => this.clients.size;

  /**
   * The subscribe method allows clients to
   * subscribe to the stream, providing update
   * and error handlers. It returns an unsubscribe
   * function for cleanup
   */
  subscribe(
    onUpdate: UpdateHandler<TData>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    const client = new StreamClient<TData>(onUpdate, onError, onMeta);
    this.clients.set(client.id, client);
    if (this.data) {
      client.updateCb(this.data, this.isSyncing(), this.total, this.sequence);
    }
    return () => {
      this.cleanup(client.id);
    };
  }

  /**
   * Is the stream currently in the
   * process of syncing.
   */
  isSyncing(): boolean {
    return this.marker === MarkerType.StartOfSync;
  }

  cleanup(id: string) {
    this.clients.delete(id);
    if (this.cleanupTimeoutId) {
      clearTimeout(this.cleanupTimeoutId);
    }
    /**
     * This will delay cleanup
     * for 10 second. That way
     * if another client gets
     * added within that 10 seconds
     * we will reuse the existing
     * Stream.
     */
    this.cleanupTimeoutId = setTimeout(() => {
      if (this.followers() === 0) {
        /**
         * Need to let any StreamManagers
         * know this stream has no more
         * clients and can be cleaned up.
         */
        this.broadcast.cancel(); // Cancel pending broadcasts
        this.fireEvent(Stream.END, this);
      }
    }, 10000);
  }

  handleError(msg: Message) {
    const error: Error | undefined = msg.error;
    if (error) {
      /**
       * 404 errors are common when it comes
       * to the candidates. this will occur
       * when the candidate config is out of
       * sync with the running config. Anything
       * missing from the running config will
       * not have operational data.
       */
      if (error?.httpStatusCode === 404) {
        logger.warn(this.message.request, error);
      } else {
        logger.error(this.message.request, error);
        /**
         * TODO: propagate errors
         */
        const apiError = ApiError.fromSyncFollowError(error);
        this.clients.forEach((client) => {
          client.errorCb(apiError);
        });
      }
    }
  }

  handleMessage(msg?: Message) {
    /**
     * If there is no message, we will
     * just propagate the existing values.
     */
    if (msg) {
      this.parseMessage(msg);
      this.count++;
      this.lastMessage = new Date();
    }
    this.broadcast();
  }

  handleMeta(msg?: Message) {
    if (msg) {
      this.total = Number(msg.data?.totalCount ?? 0); // note well: backend has typed total count as a 64-bit long
      this.sequence = Number(msg.data?.sequence ?? 0); // note well: backend has typed total count as a 64-bit long
    }
    const { total, sequence } = this;
    const syncing = this.isSyncing();
    this.clients.forEach((client) => {
      if (client.metaCb) {
        client.metaCb({ syncing, total, sequence });
      }
    });
  }
}
